<template>
  <bulk-update-wrapper
    :saving="saving"
    :title="formTitle"
    :button-label="props.buttonLabel"
    :has-changes="hasChanges"
    :show-hint="showHint"
    @save="save"
    @dismiss="dismiss"
  >
    <schema-field
      :error-schema="errorSchema"
      :form-data="customData"
      :business-unit-id="hubId"
      :params="{}"
      :root-schema="{}"
      :schema="fieldDefinitionSchema"
      :ui-schema="props.customField.uiSchema"
      :form-resolver="contextualizedFormResolver"
      data-test="bulk-update-custom-field"
      @change="updateCustomData"
    />

    <template #error-snackbar>
      <vs-snackbar :value="error" type="error" sticky @input="clearSnackbar">
        Failed to update custom fields
      </vs-snackbar>
    </template>
  </bulk-update-wrapper>
</template>

<script setup lang="ts">
import BulkUpdateWrapper from '../common/BulkUpdateWrapper.vue'
import { computed, ref, onMounted } from 'vue'
import { JobJson } from '@/models/types/job-json'
import { BulkUpdateResponse } from '@/models/types/bulk-update-response'
import { SimpleField } from '@/utils/field-schema-helper'
import { ContextualizedFormResolver } from '@/utils/forms/contextualized-form-resolver'
import { FormContext } from '@/utils/forms/form-context'
import { validateFormData } from '@/utils/forms/form-validate'
import { resolveFormDataForValidation } from '@/utils/forms/form-resolver'
import { bulkUpdateCustomField } from '@/api/jobs'

const emits = defineEmits<{
  (e: 'dismiss')
  (e: 'updated', { message, updatedJobs }: BulkUpdateResponse)
}>()

const props = defineProps<{
  buttonLabel: string
  selectedJobs: JobJson[]
  customField: SimpleField
}>()

const error = ref(false)
const saving = ref(false)
const errorSchema = ref<object>({})
const customData = ref<unknown>()
const initialCustomDataValue = ref<unknown>()
const hasDifferentValues = ref(false)

const normalizeEmptyValue = (value: unknown): unknown => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }
  return value
}

onMounted(() => {
  const normalizedValues = props.selectedJobs.map((job) =>
    normalizeEmptyValue(job.extension_data[props.customField.key]),
  )
  const selectedCustomDataValue: Set<unknown> = new Set(normalizedValues)
  hasDifferentValues.value = selectedCustomDataValue.size > 1
  if (selectedCustomDataValue.size === 1) {
    initialCustomDataValue.value = [...selectedCustomDataValue][0]
  } else {
    initialCustomDataValue.value = undefined
  }
  customData.value = initialCustomDataValue.value
})

const formTitle = computed<string>(() => {
  return props.customField.definition.title
})
const hasChanges = computed<boolean>(() => {
  return normalizeEmptyValue(customData.value) !== normalizeEmptyValue(initialCustomDataValue.value)
})
const showHint = computed<boolean>(() => {
  return hasDifferentValues.value
})

const hubId = computed<string>(() => {
  return props.selectedJobs[0].hub_id
})
const rootSchema = computed<object>(() => {
  return {
    type: 'object',
    properties: {
      [props.customField.key]: props.customField.definition,
    },
    required: props.customField.required ? [props.customField.key] : [],
  }
})

const fieldDefinitionSchema = computed<object>(() => {
  return props.customField.definition
})

// contextualizedFormResolver and validateCustomFields have been simplified because
// we know the schema wont have any nested sub schemas or dependencies
const contextualizedFormResolver = computed<ContextualizedFormResolver>(() => {
  const context = new FormContext({})
  return new ContextualizedFormResolver(context)
})
const validateCustomFields = () => {
  const resolvedData = resolveFormDataForValidation({
    [props.customField.key]: customData.value,
  })
  const errorSchema = validateFormData(rootSchema.value, resolvedData)

  return errorSchema[props.customField.key] ?? {}
}

const updateCustomData = (value) => (customData.value = value)

const clearSnackbar = () => (error.value = false)
const dismiss = () => emits('dismiss')

const save = async () => {
  saving.value = true
  try {
    errorSchema.value = validateCustomFields()
    if (Object.keys(errorSchema.value).length > 0) {
      saving.value = false
      return
    }
    await bulkUpdateCustomField(
      props.selectedJobs.map((job) => job.id),
      props.customField.key,
      customData.value,
    )
    const updatedJobs: JobJson[] = props.selectedJobs.map(({ ...job }) =>
      Object.assign(job, {
        extension_data: {
          ...job.extension_data,
          [props.customField.key]: customData.value,
        },
      }),
    )
    emits('updated', {
      message: `${props.customField.definition.title} updated`,
      updatedJobs,
    })
  } catch (err) {
    error.value = true
  } finally {
    saving.value = false
  }
}
</script>
